export type BoardViewportState = {
  scale: number;
  panX: number;
  panY: number;
};

export type BoardGridStyle = "grid" | "dots" | "lines" | "none";

export const GRID_BASE_STEP_PX = 24;

/** Крупная линия — каждая 4-я клетка (тетрадь 4x4, а не 5x5). */
export const GRID_MAJOR_EVERY = 4;

export const DEFAULT_VIEWPORT: BoardViewportState = {
  scale: 1,
  panX: 0,
  panY: 0,
};

export const SCALE_MIN = 0.1;
export const SCALE_MAX = 4;

/** Шаг кнопок +/− на панели зума (мультипликативный, как Miro). */
export const ZOOM_BUTTON_FACTOR = 1.12;

/** Чувствительность плавного зума от wheel/pinch. */
export const ZOOM_WHEEL_SENSITIVITY = 0.0016;
export const ZOOM_PINCH_SENSITIVITY = 0.009;

export function clampScale(scale: number): number {
  return Math.min(SCALE_MAX, Math.max(SCALE_MIN, scale));
}

export function clientToStage(
  clientX: number,
  clientY: number,
  viewportRect: DOMRect,
  vp: BoardViewportState
): { x: number; y: number } {
  const scale = vp.scale > 0 ? vp.scale : 1;
  const sx = clientX - viewportRect.left;
  const sy = clientY - viewportRect.top;
  // Без Math.round: целочисленные точки дают «огрызки» на рукописных штрихах.
  return {
    x: (sx - vp.panX) / scale,
    y: (sy - vp.panY) / scale,
  };
}

/** World → экран (для панели над выделением). */
export function worldToClient(
  worldX: number,
  worldY: number,
  viewportRect: DOMRect,
  vp: BoardViewportState
): { x: number; y: number } {
  const scale = vp.scale > 0 ? vp.scale : 1;
  return {
    x: viewportRect.left + vp.panX + worldX * scale,
    y: viewportRect.top + vp.panY + worldY * scale,
  };
}

/** Мультипликативный zoom к точке на экране (плавно, без рывков). */
export function viewportZoomByFactor(
  vp: BoardViewportState,
  viewportRect: DOMRect,
  clientX: number,
  clientY: number,
  factor: number
): BoardViewportState {
  if (!Number.isFinite(factor) || factor <= 0 || factor === 1) return vp;
  const newScale = clampScale(vp.scale * factor);
  if (newScale === vp.scale) return vp;
  const mx = clientX - viewportRect.left;
  const my = clientY - viewportRect.top;
  const ratio = newScale / vp.scale;
  return {
    scale: newScale,
    panX: mx - ratio * (mx - vp.panX),
    panY: my - ratio * (my - vp.panY),
  };
}

/**
 * Аддитивный delta → через factor (совместимость со старыми вызовами zoomBy(±0.1)).
 */
export function viewportZoomAtPoint(
  vp: BoardViewportState,
  viewportRect: DOMRect,
  clientX: number,
  clientY: number,
  scaleDelta: number
): BoardViewportState {
  if (!scaleDelta) return vp;
  const target = clampScale(vp.scale + scaleDelta);
  if (target === vp.scale || vp.scale <= 0) return vp;
  return viewportZoomByFactor(vp, viewportRect, clientX, clientY, target / vp.scale);
}

export function viewportCenterOnOrigin(
  vp: BoardViewportState,
  viewportWidth: number,
  viewportHeight: number
): BoardViewportState {
  return {
    ...vp,
    panX: viewportWidth / 2,
    panY: viewportHeight / 2,
  };
}

export function gridScreenStepPx(scale: number): number {
  return GRID_BASE_STEP_PX * scale;
}

const GRID_FINE_HIDE_BELOW = 8;
const GRID_FINE_FULL_BELOW = 14;

export type GridDisplayState = {
  /** Шаг в мировых px (сетка внутри camera-transform). */
  microStepPx: number;
  fineStepPx: number;
  majorStepPx: number;
  /** Толщина линии в мировых px ≈ 1 CSS-px на экране. */
  lineWidthPx: number;
  microOpacity: number;
  fineOpacity: number;
  majorOpacity: number;
};

/**
 * Лёгкая клеточная сетка с LOD (как в Miro):
 * при зуме между крупными клетками плавно проявляются более мелкие.
 * Шаги — в world-space: слой сетки живёт внутри camera transform.
 */
export function computeGridDisplay(scale: number): GridDisplayState {
  const safeScale = scale > 0 ? scale : 1;

  // Клетка у доски одна и та же — всегда `GRID_BASE_STEP_PX` в мировых
  // единицах, независимо от того, кто как приблизил.
  //
  // Раньше шаг подбирался под масштаб: у преподавателя при 100% клетка была
  // одна, у ученика при 50% — вдвое крупнее. График, построенный «на две
  // клетки вверх», у другого человека оказывался на одну, а сам
  // преподаватель после приближения не находил те же клетки. Прилипание при
  // этом всегда считалось по постоянному шагу — то есть рисунок липнул к
  // одной сетке, а показывали другую.
  //
  // Теперь приближение только увеличивает клетки, а не меняет их.
  const worldFine = GRID_BASE_STEP_PX;
  const worldMicro = worldFine / 2;
  const worldMajor = worldFine * GRID_MAJOR_EVERY;

  const fineScreen = worldFine * safeScale;

  // Основная клетка: видна в комфортном диапазоне
  let fineOpacity = 1;
  if (fineScreen < GRID_FINE_HIDE_BELOW) {
    fineOpacity = 0;
  } else if (fineScreen < GRID_FINE_FULL_BELOW) {
    fineOpacity = clamp01(
      (fineScreen - GRID_FINE_HIDE_BELOW) / (GRID_FINE_FULL_BELOW - GRID_FINE_HIDE_BELOW)
    );
  }

  // Половинок клетки больше нет: лишняя линия при приближении — это ровно то,
  // из-за чего казалось, что клетки «поехали».
  const microOpacity = 0;

  // Крупная (каждая 4-я) — чуть заметнее, но всё равно лёгкая
  const majorOpacity = clamp01(0.4 + fineOpacity * 0.5);

  return {
    microStepPx: worldMicro,
    fineStepPx: worldFine,
    majorStepPx: worldMajor,
    lineWidthPx: 1 / safeScale,
    microOpacity,
    fineOpacity,
    majorOpacity,
  };
}

function clamp01(v: number): number {
  return Math.min(1, Math.max(0, v));
}

/**
 * Сетка на viewport-canvas в экранных координатах.
 * Перерисовка раз в кадр (через rAF снаружи) — визуально корректный pan/zoom.
 */
export function paintBoardGridCanvas(
  canvas: HTMLCanvasElement,
  vp: BoardViewportState,
  cssW: number,
  cssH: number,
  opts?: { light?: boolean; style?: BoardGridStyle }
): void {
  if (cssW < 1 || cssH < 1) return;
  const style = opts?.style ?? "grid";
  const dpr = Math.min(typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1, 2);
  const tw = Math.max(1, Math.floor(cssW * dpr));
  const th = Math.max(1, Math.floor(cssH * dpr));
  if (canvas.width !== tw || canvas.height !== th) {
    canvas.width = tw;
    canvas.height = th;
  }
  canvas.style.width = `${cssW}px`;
  canvas.style.height = `${cssH}px`;
  canvas.style.left = "0";
  canvas.style.top = "0";
  canvas.style.transform = "";

  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, cssW, cssH);

  if (style === "none") return;

  const g = computeGridDisplay(vp.scale);
  const scale = vp.scale > 0 ? vp.scale : 1;

  const strokeLevel = (stepWorld: number, opacity: number, alphaBase: number) => {
    if (opacity < 0.02) return;
    const step = stepWorld * scale;
    if (step < 6) return;
    ctx.strokeStyle = `rgba(15, 23, 42, ${alphaBase * opacity})`;
    ctx.lineWidth = 1;
    const startX = ((vp.panX % step) + step) % step;
    const startY = ((vp.panY % step) + step) % step;
    ctx.beginPath();
    for (let x = startX; x <= cssW + 0.5; x += step) {
      ctx.moveTo(x, 0);
      ctx.lineTo(x, cssH);
    }
    for (let y = startY; y <= cssH + 0.5; y += step) {
      ctx.moveTo(0, y);
      ctx.lineTo(cssW, y);
    }
    ctx.stroke();
  };

  const strokeHorizontal = (stepWorld: number, opacity: number, alphaBase: number, majorEvery = GRID_MAJOR_EVERY) => {
    if (opacity < 0.02) return;
    const step = stepWorld * scale;
    if (step < 6) return;
    const startY = ((vp.panY % step) + step) % step;
    let i = 0;
    for (let y = startY; y <= cssH + 0.5; y += step) {
      const isMajor = majorEvery > 1 && i % majorEvery === 0;
      ctx.strokeStyle = `rgba(15, 23, 42, ${alphaBase * opacity * (isMajor ? 1.35 : 0.85)})`;
      ctx.lineWidth = isMajor ? 1.15 : 1;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(cssW, y);
      ctx.stroke();
      i += 1;
    }
  };

  const paintDots = (stepWorld: number, opacity: number, alphaBase: number) => {
    if (opacity < 0.02) return;
    const step = stepWorld * scale;
    if (step < 10) return;
    const startX = ((vp.panX % step) + step) % step;
    const startY = ((vp.panY % step) + step) % step;
    const r = Math.max(0.75, Math.min(1.4, step * 0.07));
    ctx.fillStyle = `rgba(15, 23, 42, ${alphaBase * opacity})`;
    for (let x = startX; x <= cssW + 0.5; x += step) {
      for (let y = startY; y <= cssH + 0.5; y += step) {
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  };

  if (style === "dots") {
    paintDots(g.fineStepPx, g.fineOpacity, opts?.light ? 0.14 : 0.12);
    if (!opts?.light && g.microOpacity > 0.15) {
      paintDots(g.microStepPx, g.microOpacity * 0.55, 0.08);
    }
    return;
  }

  if (style === "lines") {
    strokeHorizontal(g.fineStepPx, g.fineOpacity, opts?.light ? 0.08 : 0.07, GRID_MAJOR_EVERY);
    return;
  }

  if (!opts?.light) {
    strokeLevel(g.microStepPx, g.microOpacity, 0.04);
  }
  strokeLevel(g.fineStepPx, g.fineOpacity, opts?.light ? 0.06 : 0.055);
  strokeLevel(g.majorStepPx, g.majorOpacity, 0.09);
}

export function formatScalePct(scale: number): string {
  return `${Math.round(scale * 100)}%`;
}

export type WheelGesture =
  | { kind: "pan"; deltaX: number; deltaY: number }
  | { kind: "zoom"; clientX: number; clientY: number; factor: number };

/** Классические «зубья» колёсика (Windows/Chrome часто 100 или 120). */
function isNotchyMouseWheelDelta(absDy: number): boolean {
  for (const step of [100, 120]) {
    const n = absDy / step;
    if (n >= 0.95 && n <= 4.05 && Math.abs(n - Math.round(n)) < 0.02) {
      return true;
    }
  }
  return false;
}

/**
 * Разбор wheel:
 * - pinch на тачпаде (Ctrl/Meta) → zoom;
 * - колёсико мыши → zoom к курсору;
 * - два пальца по тачпаду (плавный pixel-scroll) → pan.
 *
 * Важно: на Windows тачпад тоже шлёт deltaMode=0 с крупным |deltaY| без X —
 * нельзя считать «большой Y без X» мышью (из‑за этого pan + zoom разом).
 */
export function resolveWheelGesture(ev: {
  deltaX: number;
  deltaY: number;
  deltaMode: number;
  ctrlKey: boolean;
  metaKey: boolean;
  shiftKey?: boolean;
  clientX: number;
  clientY: number;
}): WheelGesture {
  const zoomIntent = ev.ctrlKey || ev.metaKey;
  const linePx = ev.deltaMode === 1 ? 16 : ev.deltaMode === 2 ? 800 : 1;
  const dxPx = ev.deltaX * linePx;
  const dyPx = ev.deltaY * linePx;
  const absDx = Math.abs(dxPx);
  const absDy = Math.abs(dyPx);

  if (zoomIntent) {
    if (!dyPx) {
      return { kind: "pan", deltaX: -dxPx, deltaY: -dyPx };
    }
    return {
      kind: "zoom",
      clientX: ev.clientX,
      clientY: ev.clientY,
      factor: Math.exp(-dyPx * ZOOM_PINCH_SENSITIVITY),
    };
  }

  // LINE/PAGE — почти всегда мышь; pixel — только «зубчатые» 100/120.
  const looksLikeMouseWheel =
    ev.deltaMode === 1 ||
    ev.deltaMode === 2 ||
    (ev.deltaMode === 0 && absDx < 0.5 && isNotchyMouseWheelDelta(absDy));

  if (looksLikeMouseWheel && dyPx) {
    return {
      kind: "zoom",
      clientX: ev.clientX,
      clientY: ev.clientY,
      factor: Math.exp(-dyPx * ZOOM_WHEEL_SENSITIVITY),
    };
  }

  // Тачпад / плавный pixel-scroll.
  return { kind: "pan", deltaX: -dxPx, deltaY: -dyPx };
}

/** Шаг двухпальцевого pan + pinch zoom (экранные координаты). */
export function applyTouchNavStep(
  vp: BoardViewportState,
  viewportRect: DOMRect,
  prev: { midX: number; midY: number; dist: number },
  next: { midX: number; midY: number; dist: number }
): BoardViewportState {
  let out = vp;
  const dx = next.midX - prev.midX;
  const dy = next.midY - prev.midY;
  if (dx || dy) {
    out = {
      ...out,
      panX: out.panX + dx,
      panY: out.panY + dy,
    };
  }
  if (prev.dist > 1 && next.dist > 1) {
    const factor = next.dist / prev.dist;
    if (Number.isFinite(factor) && Math.abs(factor - 1) > 1e-4) {
      out = viewportZoomByFactor(out, viewportRect, next.midX, next.midY, factor);
    }
  }
  return out;
}

export function applyWheelGesture(
  vp: BoardViewportState,
  viewportRect: DOMRect,
  gesture: WheelGesture
): BoardViewportState {
  if (gesture.kind === "pan") {
    if (!gesture.deltaX && !gesture.deltaY) return vp;
    return {
      ...vp,
      panX: vp.panX + gesture.deltaX,
      panY: vp.panY + gesture.deltaY,
    };
  }
  return viewportZoomByFactor(
    vp,
    viewportRect,
    gesture.clientX,
    gesture.clientY,
    gesture.factor
  );
}

export type WorldBounds = { x: number; y: number; w: number; h: number };

/** Видимая область viewport в мировых координатах. */
export function visibleWorldFromViewport(
  viewportWidth: number,
  viewportHeight: number,
  vp: BoardViewportState
): WorldBounds {
  const scale = vp.scale > 0 ? vp.scale : 1;
  return {
    x: -vp.panX / scale,
    y: -vp.panY / scale,
    w: Math.max(1, viewportWidth / scale),
    h: Math.max(1, viewportHeight / scale),
  };
}

/** Центрировать точку мира, сохранив текущий scale. */
export function panToWorldPoint(
  viewportWidth: number,
  viewportHeight: number,
  vp: BoardViewportState,
  worldX: number,
  worldY: number
): BoardViewportState {
  const scale = vp.scale > 0 ? vp.scale : 1;
  return {
    scale,
    panX: viewportWidth / 2 - worldX * scale,
    panY: viewportHeight / 2 - worldY * scale,
  };
}

/**
 * Подогнать вьюпорт под AABB в мировых координатах (с отступом в px экрана).
 */
export function fitWorldBounds(
  viewportWidth: number,
  viewportHeight: number,
  bounds: WorldBounds,
  paddingPx = 56
): BoardViewportState {
  const bw = Math.max(1, bounds.w);
  const bh = Math.max(1, bounds.h);
  const pad = Math.max(0, paddingPx);
  const availW = Math.max(1, viewportWidth - pad * 2);
  const availH = Math.max(1, viewportHeight - pad * 2);
  const scale = clampScale(Math.min(availW / bw, availH / bh));
  const cx = bounds.x + bw / 2;
  const cy = bounds.y + bh / 2;
  return {
    scale,
    panX: viewportWidth / 2 - cx * scale,
    panY: viewportHeight / 2 - cy * scale,
  };
}
